import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useUserProfile } from './useUserProfile';

export function useSubscriptionLimits() {
  const { tenant } = useUserProfile();

  const { data: plan, isLoading } = useQuery({
    queryKey: ['subscription_plan', tenant?.subscription_plan_id],
    queryFn: async () => {
      if (!tenant?.subscription_plan_id) return null;

      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('id', tenant.subscription_plan_id)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!tenant?.subscription_plan_id,
  });

  const checkUserLimit = async (): Promise<boolean> => {
    if (!tenant || !plan) return true; // Sem limite se não houver plano

    const { count, error } = await supabase
      .from('user_roles')
      .select('*', { count: 'exact', head: true })
      .eq('tenant_id', tenant.id);

    if (error) throw error;
    return (count || 0) < plan.max_users;
  };

  const checkIntegrationLimit = async (): Promise<boolean> => {
    if (!tenant || !plan) return true;

    const { count, error } = await supabase
      .from('integrations')
      .select('*', { count: 'exact', head: true })
      .eq('tenant_id', tenant.id)
      .eq('status_active', true);

    if (error) throw error;
    return (count || 0) < plan.max_integrations;
  };

  const checkGoalLimit = async (): Promise<boolean> => {
    if (!tenant || !plan || !plan.goals_enabled) return false;

    const { count, error } = await supabase
      .from('business_goals')
      .select('*', { count: 'exact', head: true })
      .eq('tenant_id', tenant.id)
      .eq('is_active', true);

    if (error) throw error;
    
    // 0 means unlimited
    if (plan.max_goals === 0) return true;
    
    return (count || 0) < plan.max_goals;
  };

  const checkProductLimit = async (): Promise<boolean> => {
    if (!tenant || !plan) return true;

    // 0 means unlimited
    if (plan.max_products === 0) return true;

    const { count, error } = await supabase
      .from('products')
      .select('*', { count: 'exact', head: true })
      .eq('tenant_id', tenant.id)
      .eq('is_active', true);

    if (error) throw error;
    return (count || 0) < plan.max_products;
  };

  const isIntegrationAllowed = (provider: string): boolean => {
    if (!plan) return true;
    const integrations = plan.allowed_integrations as unknown as string[];
    return integrations.includes(provider);
  };

  const hasAdvancedMetric = (metric: string): boolean => {
    if (!plan) return false;
    const metrics = plan.advanced_metrics as unknown as string[];
    return metrics.includes(metric);
  };

  return {
    plan,
    isLoading,
    canAddUser: async () => await checkUserLimit(),
    canAddIntegration: async () => await checkIntegrationLimit(),
    canAddGoal: async () => await checkGoalLimit(),
    canAddProduct: async () => await checkProductLimit(),
    isIntegrationAllowed,
    hasAdvancedMetric,
    getBasicMetrics: () => (plan?.basic_metrics as string[]) ?? [],
    getAdvancedMetricsConfig: () => ({
      enabled: plan?.advanced_metrics_enabled ?? false,
      mode: plan?.advanced_metrics_mode ?? 'predefined',
      metrics: (plan?.advanced_metrics as string[]) ?? [],
      maxMetrics: plan?.max_advanced_metrics ?? 0,
    }),
    getIntegrationsConfig: () => ({
      mode: plan?.integrations_mode ?? 'predefined',
      allowed: (plan?.allowed_integrations as string[]) ?? [],
      maxIntegrations: plan?.max_integrations ?? 0,
    }),
    getGoalsConfig: () => ({
      enabled: plan?.goals_enabled ?? false,
      maxGoals: plan?.max_goals ?? 0,
    }),
    getPricing: () => ({
      monthly: plan?.price_monthly ?? 0,
      annual: plan?.price_annual ?? 0,
      lifetime: plan?.price_lifetime ?? 0,
    }),
    manualEntryEnabled: plan?.manual_entry_enabled ?? true,
    bulkImportEnabled: plan?.bulk_import_enabled ?? false,
    prioritySupport: plan?.priority_support ?? false,
    maxUsers: plan?.max_users ?? 0,
    maxProducts: plan?.max_products ?? 0,
  };
}
